import React from 'react';
import { Briefcase, X, MessageSquare, FileText, Layers } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../ui/select';
import { LETTER_TYPES, MESSAGE_SCENARIOS } from '../../constants/mediscribe';

// Filter out provider-only letter types for patient mode
const PROVIDER_ONLY_VALUES = [
  'consult_behavioral_health', 'consult_pharmacy', 'consult_specialist',
  'consult_peer_question', 'consult_patient_handoff', 'consult_medication_review',
  'provider_consult',
];
const PATIENT_LETTER_TYPES = LETTER_TYPES.filter(lt => !PROVIDER_ONLY_VALUES.includes(lt.value));

/**
 * PatientLetterOptions - Document type, letter type, and message scenario options for Patient mode
 */
export const PatientLetterOptions = ({
  letterType,
  setLetterType,
  setGenerateAdminLetter,
  letterTone,
  setLetterTone,
  letterPageLimit,
  setLetterPageLimit,
  includeCoverLetter,
  setIncludeCoverLetter,
  letterFormat,
  setLetterFormat,
  referencePosition,
  setReferencePosition,
  // Document mode
  documentMode,
  setDocumentMode,
  messageScenario,
  setMessageScenario,
}) => {
  const mode = documentMode || 'letter';
  const showLetterOptions = mode === 'letter' || mode === 'both';
  const showMessageOptions = mode === 'message' || mode === 'both';
  const selectedScenario = MESSAGE_SCENARIOS.find(s => s.value === messageScenario);

  return (
    <div className="space-y-4">
      {/* Document Mode Selector */}
      <div className="flex gap-2 p-1 bg-secondary/40 rounded-lg border border-border/50" data-testid="document-mode-selector">
        {[
          { value: 'letter', label: 'Letter', icon: FileText, desc: 'Formal document for provider' },
          { value: 'message', label: 'Portal Message', icon: MessageSquare, desc: 'Short message for clinic inbox' },
          { value: 'both', label: 'Both', icon: Layers, desc: 'Letter + portal message' },
        ].map(opt => {
          const Icon = opt.icon;
          const isActive = mode === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setDocumentMode(opt.value)}
              className={`flex-1 flex flex-col items-center gap-1 py-2.5 px-2 rounded-md transition-all text-center ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
              }`}
              data-testid={`doc-mode-${opt.value}`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-xs font-medium">{opt.label}</span>
            </button>
          );
        })}
      </div>

      {/* Portal Message Scenario Section */}
      {showMessageOptions && (
        <div className="p-3 bg-blue-500/5 border border-blue-500/30 rounded-lg" data-testid="message-scenario-section">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400 font-medium text-sm">Portal Message</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Select what you need to communicate. This generates a short, clear message you can paste into your patient portal (MHS Genesis, MyChart, etc.).
          </p>

          <Select value={messageScenario || ''} onValueChange={setMessageScenario}>
            <SelectTrigger className="h-9 text-sm bg-input" data-testid="message-scenario-select">
              <SelectValue placeholder="What do you need to communicate?" />
            </SelectTrigger>
            <SelectContent className="max-h-[400px]">
              <SelectGroup>
                <SelectLabel className="text-primary">Medication</SelectLabel>
                {MESSAGE_SCENARIOS.filter(s => s.category === 'Medication').map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectGroup>
              <SelectGroup>
                <SelectLabel className="text-green-500">Appointments & Follow-ups</SelectLabel>
                {MESSAGE_SCENARIOS.filter(s => s.category === 'Appointment').map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectGroup>
              <SelectGroup>
                <SelectLabel className="text-amber-500">Results & Records</SelectLabel>
                {MESSAGE_SCENARIOS.filter(s => s.category === 'Results').map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectGroup>
              <SelectGroup>
                <SelectLabel className="text-orange-500">Profile & Duty</SelectLabel>
                {MESSAGE_SCENARIOS.filter(s => s.category === 'Profile & Duty').map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectGroup>
              <SelectGroup>
                <SelectLabel className="text-muted-foreground">Administrative</SelectLabel>
                {MESSAGE_SCENARIOS.filter(s => s.category === 'Administrative').map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          {/* Selected scenario guidance */}
          {selectedScenario && (
            <div className="mt-3 space-y-2">
              <div className="text-xs text-muted-foreground p-2 bg-secondary/50 rounded border border-border">
                <p className="font-semibold mb-1 text-blue-400">Include in your description:</p>
                <p>{selectedScenario.prompt}</p>
              </div>
              <div className="text-[10px] text-muted-foreground/60 p-2 bg-secondary/30 rounded">
                <p className="font-medium mb-1">Example message:</p>
                <p className="italic">{selectedScenario.example}</p>
              </div>
            </div>
          )}

          {messageScenario && (
            <Button variant="ghost" size="sm" onClick={() => setMessageScenario('')}
              className="mt-2 h-7 px-2 text-xs text-muted-foreground hover:text-destructive" data-testid="clear-message-scenario">
              <X className="w-3 h-3 mr-1" /> Clear scenario
            </Button>
          )}
        </div>
      )}

      {/* Letter Options Section (existing) */}
      {showLetterOptions && (
        <div className="p-3 bg-primary/5 border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors" data-testid="letter-options-section">
          <div className="flex items-center gap-2 mb-3">
            <Briefcase className="w-4 h-4 text-primary" />
            <span className="text-primary font-medium text-sm">Letter Options</span>
          </div>
          <div className="space-y-3">
            {/* Letter Type */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Letter Type</Label>
              <div className="flex gap-2">
                <Select value={letterType} onValueChange={(val) => { setLetterType(val); setGenerateAdminLetter(true); }}>
                  <SelectTrigger className="h-9 text-sm bg-input flex-1" data-testid="letter-type-select"><SelectValue placeholder="Select letter type..." /></SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    <SelectGroup>
                      <SelectItem value="custom">Custom Letter</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectGroup>
                    <SelectGroup>
                      {/* v6.8.46 — Dropdown group labels were rendering text-primary
                          (amber/yellow) on a light popover surface = white-on-yellow
                          unreadable. Each label now uses a pill-style chip with a
                          contrasting background + dark foreground so the section
                          boundaries are obvious at every popover-background color. */}
                      <SelectLabel className="text-[11px] uppercase tracking-wider font-bold text-sky-700 bg-sky-100 dark:text-sky-100 dark:bg-sky-900/70 rounded px-2 py-0.5 mx-1 my-1 inline-block">Return to Work</SelectLabel>
                      {PATIENT_LETTER_TYPES.filter(lt => lt.category === 'Return to Work').map((lt) => <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>)}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel className="text-[11px] uppercase tracking-wider font-bold text-emerald-800 bg-emerald-100 dark:text-emerald-100 dark:bg-emerald-900/70 rounded px-2 py-0.5 mx-1 my-1 inline-block">Civilian / Disability</SelectLabel>
                      {PATIENT_LETTER_TYPES.filter(lt => lt.category === 'Civilian').map((lt) => <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>)}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel className="text-[11px] uppercase tracking-wider font-bold text-amber-800 bg-amber-100 dark:text-amber-100 dark:bg-amber-900/70 rounded px-2 py-0.5 mx-1 my-1 inline-block">Workplace / FMLA</SelectLabel>
                      {PATIENT_LETTER_TYPES.filter(lt => lt.category === 'Workplace').map((lt) => <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>)}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel className="text-[11px] uppercase tracking-wider font-bold text-indigo-800 bg-indigo-100 dark:text-indigo-100 dark:bg-indigo-900/70 rounded px-2 py-0.5 mx-1 my-1 inline-block">Military</SelectLabel>
                      {PATIENT_LETTER_TYPES.filter(lt => lt.category === 'Military').map((lt) => <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>)}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {letterType && (
                  <Button variant="ghost" size="sm" onClick={() => { setLetterType(''); setGenerateAdminLetter(false); }} className="h-9 px-2 text-muted-foreground hover:text-destructive" title="Clear">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Letter Format & Reference Position */}
            {letterType && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Format Style</Label>
                  <Select value={letterFormat || 'plain'} onValueChange={setLetterFormat}>
                    <SelectTrigger className="h-8 text-xs bg-input" data-testid="letter-format-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plain">Plain Letter</SelectItem>
                      <SelectItem value="military_memo">Military Memo (AR 25-50)</SelectItem>
                      <SelectItem value="military_simple">Military Simple</SelectItem>
                      <SelectItem value="figure_3_1">Figure 3-1 (Official)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">References</Label>
                  <Select value={referencePosition || 'none'} onValueChange={setReferencePosition}>
                    <SelectTrigger className="h-8 text-xs bg-input" data-testid="reference-position-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No References</SelectItem>
                      <SelectItem value="bottom">At Bottom</SelectItem>
                      <SelectItem value="top">At Top (Formal/iPERMS)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Letter Style Options inline */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">Tone</Label>
                <Select value={letterTone} onValueChange={setLetterTone}>
                  <SelectTrigger className="h-8 text-xs bg-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="civilian">Civilian/Plain</SelectItem>
                    <SelectItem value="formal">Formal</SelectItem>
                    <SelectItem value="detailed">Detailed</SelectItem>
                    <SelectItem value="concise">Concise</SelectItem>
                    <SelectItem value="concern">Concern/Urgent</SelectItem>
                    <SelectItem value="neutral">Neutral</SelectItem>
                    <SelectItem value="empathetic">Empathetic</SelectItem>
                    <SelectItem value="mfr">MFR (Memorandum for Record)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">Page Limit</Label>
                <Select value={letterPageLimit} onValueChange={setLetterPageLimit}>
                  <SelectTrigger className="h-8 text-xs bg-input"><SelectValue placeholder="No limit" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No limit</SelectItem>
                    <SelectItem value="1">1 page max</SelectItem>
                    <SelectItem value="2">2 pages max</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Format hint based on selection */}
            {letterType && letterFormat && (
              <div className="text-[10px] text-muted-foreground/70 px-2 py-1 bg-secondary/30 rounded">
                {letterFormat === 'plain' && "Simple letter format - content first, no military formatting"}
                {letterFormat === 'military_memo' && "Full AR 25-50 memorandum with header block"}
                {letterFormat === 'military_simple' && "AR 25-50 style but streamlined for quick reading"}
                {letterFormat === 'figure_3_1' && "Official correspondence template per AR 25-50 Figure 3-1"}
              </div>
            )}

            {/* Guided prompts based on letter type */}
            {letterType && (
              <div className="text-xs text-muted-foreground p-2 bg-secondary/50 rounded border border-border">
                <p className="font-semibold mb-1">Include in your description:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {letterType === 'ada_accommodations' && (
                    <>
                      <li>Your employer's name & address</li>
                      <li>What accommodation you need</li>
                      <li>How long you've had this condition</li>
                      <li>Are you seeing a specialist? How often?</li>
                    </>
                  )}
                  {letterType === 'fmla_intermittent' && (
                    <>
                      <li>Your employer's name & address</li>
                      <li>Type of condition (don't need diagnosis)</li>
                      <li>How long you've been dealing with this</li>
                      <li>Frequency of flare-ups/appointments</li>
                    </>
                  )}
                  {(letterType === 'deployment_deletion' || letterType === 'medical_stabilization') && (
                    <>
                      <li>Your condition/symptoms</li>
                      <li>When did this start?</li>
                      <li>Current treatment/medications</li>
                      <li>Upcoming surgeries or procedures?</li>
                    </>
                  )}
                  {letterType === 'custom' && (
                    <>
                      <li>What the letter is for</li>
                      <li>Your condition (general terms ok)</li>
                      <li>Duration of condition</li>
                      <li>What you need the provider to support</li>
                    </>
                  )}
                  {letterType === 'letter_to_command' && (
                    <>
                      <li>Who this letter is for (Commander, 1SG, Supervisor)</li>
                      <li>What you're requesting</li>
                      <li>Your current limitations/profile restrictions</li>
                    </>
                  )}
                  {!['ada_accommodations', 'fmla_intermittent', 'deployment_deletion', 'medical_stabilization', 'custom', 'letter_to_command', 'other', 'referral_request'].includes(letterType) && (
                    <>
                      <li>Your condition and symptoms</li>
                      <li>How long you've had this condition</li>
                      <li>What support/documentation you need</li>
                    </>
                  )}
                  {letterType === 'other' && (
                    <>
                      <li>What the letter is for</li>
                      <li>Who this letter is going to</li>
                      <li>Your condition (general terms ok)</li>
                      <li>What you need the provider to support</li>
                    </>
                  )}
                  {letterType === 'referral_request' && (
                    <>
                      <li>Which specialty you need a referral for</li>
                      <li>How long you've been waiting or what the issue is</li>
                      <li>Your symptoms and how they affect daily life</li>
                      <li>Any previous referrals or treatments tried</li>
                    </>
                  )}
                </ul>
              </div>
            )}

            {/* Cover Letter Option */}
            {letterType && (
              <div className="flex items-center space-x-2 pt-2 border-t border-border/30">
                <Checkbox id="include-cover" checked={includeCoverLetter} onCheckedChange={setIncludeCoverLetter} data-testid="checkbox-cover-letter" />
                <label htmlFor="include-cover" className="text-xs text-muted-foreground cursor-pointer">
                  Include cover letter explaining this is a draft for provider review
                </label>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientLetterOptions;
